import { useEffect, useState } from 'react';
import { Bell, Check, Plus, X } from 'lucide-react';
import { Button, Card, Input, Modal, Pill, useToast } from '@nako/ui';
import { formatMoney } from '@nako/core';
import type { Reminder, ReminderType } from '@nako/types';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { supabase } from '@/lib/supabase';

const TYPES: ReminderType[] = ['vat', 'paye', 'income_tax', 'salary', 'supplier', 'custom'];

export default function Reminders() {
  const business = useAuthStore((s) => s.business)!;
  const regionCode = useRegionStore((s) => s.code);
  const toast = useToast();
  const [items, setItems] = useState<Reminder[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<{
    type: ReminderType;
    title: string;
    amount: string;
    due: string;
  }>({ type: 'custom', title: '', amount: '', due: '' });

  useEffect(() => {
    void load();
  }, [business.id]);

  async function load() {
    const { data } = await supabase
      .from('reminders')
      .select('*')
      .eq('business_id', business.id)
      .order('due_date', { ascending: true });
    setItems(data ?? []);
  }

  async function create() {
    if (!form.title || !form.due) {
      toast.show('Title and due date required', { tone: 'warning' });
      return;
    }
    await supabase.from('reminders').insert({
      business_id: business.id,
      type: form.type,
      title: form.title,
      amount: form.amount ? Number(form.amount) : null,
      due_date: form.due,
    });
    setOpen(false);
    setForm({ type: 'custom', title: '', amount: '', due: '' });
    void load();
  }

  async function setStatus(id: string, status: 'done' | 'dismissed') {
    await supabase
      .from('reminders')
      .update({ status, completed_at: status === 'done' ? new Date().toISOString() : null })
      .eq('id', id);
    void load();
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display flex items-center gap-2 text-2xl">
          <Bell size={20} /> Reminders
        </h1>
        <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setOpen(true)}>
          New
        </Button>
      </div>

      {items.length === 0 ? (
        <Card className="py-10 text-center">
          <Bell size={32} className="text-muted mx-auto mb-3 opacity-50" />
          <p className="text-muted mb-4">
            No reminders yet. Add VAT, PAYE, supplier or custom dates.
          </p>
          <Button onClick={() => setOpen(true)} leftIcon={<Plus size={16} />}>
            Add your first reminder
          </Button>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((r) => {
            const days = Math.ceil((new Date(r.due_date).getTime() - Date.now()) / 86_400_000);
            const tone =
              r.status === 'done' ? 'green' : days < 0 ? 'red' : days <= 3 ? 'accent' : 'blue';
            return (
              <Card key={r.id} compact className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{r.title}</p>
                    <Pill tone="neutral" className="text-[10px]">
                      {r.type}
                    </Pill>
                  </div>
                  <p className="text-muted text-xs">
                    Due {r.due_date}
                    {r.amount != null && ` · ${formatMoney(r.amount, regionCode)}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Pill tone={tone}>
                    {r.status === 'done' ? 'done' : days < 0 ? `${-days}d late` : `${days}d`}
                  </Pill>
                  {r.status !== 'done' && (
                    <>
                      <button
                        onClick={() => setStatus(r.id, 'done')}
                        className="text-green"
                        aria-label="Mark done"
                      >
                        <Check size={18} />
                      </button>
                      <button
                        onClick={() => setStatus(r.id, 'dismissed')}
                        className="text-muted"
                        aria-label="Dismiss"
                      >
                        <X size={18} />
                      </button>
                    </>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New reminder">
        <div className="space-y-3">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as ReminderType })}
              className="min-h-tap rounded-btn border-line border bg-white px-3"
            >
              {TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
          <Input
            label="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <Input
            label="Due date"
            type="date"
            value={form.due}
            onChange={(e) => setForm({ ...form, due: e.target.value })}
          />
          <Input
            label="Amount (optional)"
            type="number"
            numeric
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            leftAddon="P"
          />
          <Button full onClick={create}>
            Create
          </Button>
        </div>
      </Modal>
    </main>
  );
}
